import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { tipTapToPlainText } from "@compass/shared/utils/tipTapPlainText";
import {
    Check,
    Clock,
    Copy,
    MessageSquare,
    Paperclip,
    Plus,
    RotateCcw,
    Share2,
    Trash2,
    X,
} from "lucide-react";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import AskNavigatorIcon from "assets/img/icons/ask-navigator.svg?react";
import SubmitIcon from "assets/img/icons/submit.svg?react";
import { Button, Spinner, TextArea } from "components/elements";
import { MarkdownContent, ProseDocument } from "components/elements";
import { DeleteDialogModal } from "components/elements/DeleteDialogModal";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "components/shad-components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "components/shad-components/ui/toggle-group";
import { downscaleImageForUpload } from "lib/downscaleImage";
import { formatISODateRelative } from "lib/formatISODate";
import { type RouterOutputs, trpc } from "lib/trpc";
import { VendorDetailedEntry } from "screens/vendors/VendorDetailedEntry";

type AskResult = RouterOutputs["askNavigator"]["ask"];
type ChatTurn = RouterOutputs["askNavigator"]["getChat"]["turns"][number];
type AskScope = ChatTurn["scope"];
type BlogPostEntry = AskResult["blogPosts"][number];
type VendorEntry = NonNullable<AskResult["vendorParams"]>["vendors"][number];

/**
 * The answer half of a turn. A live ask carries blog posts and vendor rows;
 * a turn reopened from history only has what the chat stored (contextResults),
 * so those arrays are absent there and the sources render from what exists.
 */
type TurnAnswer = {
    queryType: ChatTurn["queryType"];
    answerJson: TipTapDoc | null;
    markdownAnswer: string | null;
    suggestedQuestions: string[];
    contextResults: ChatTurn["contextResults"];
    blogPosts?: BlogPostEntry[];
    vendors?: VendorEntry[];
};

type SelectedFile = {
    name: string;
    previewUrl: string | null;
    file: File;
};

type Turn = {
    key: string;
    question: string;
    scope: AskScope;
    status: "pending" | "done" | "error";
    answer: TurnAnswer | null;
    // Kept so a failed turn can be retried with the same attachment.
    file?: SelectedFile | null;
    imageFileId?: string;
};

const answerFromAsk = (result: AskResult): TurnAnswer => ({
    queryType: result.queryType,
    answerJson: result.answerJson ?? null,
    markdownAnswer: result.markdownAnswer ?? null,
    suggestedQuestions: result.suggestedQuestions,
    contextResults: result.contextResults,
    blogPosts: result.blogPosts,
    vendors: result.vendorParams?.vendors ?? [],
});

const turnFromHistory = (turn: ChatTurn): Turn => ({
    key: `saved-${turn.id}`,
    question: turn.question,
    scope: turn.scope,
    status: "done",
    answer: {
        queryType: turn.queryType,
        answerJson: turn.answerJson,
        markdownAnswer: turn.markdownAnswer,
        suggestedQuestions: turn.suggestedQuestions,
        contextResults: turn.contextResults,
    },
});

const plainAnswer = (answer: TurnAnswer) =>
    (answer.answerJson ? tipTapToPlainText(answer.answerJson) : "") || answer.markdownAnswer || "";

const suggestions = [
    "How are Financial Advisors using AI to run their practices?",
    "What is the average pay for a Financial Advisor?",
    "What is the best software out there for compliance?",
    "What CRM softwares do financial advisors use?",
    "What is the average rate that Financial Advisors charge their clients?",
    "Are there any RIA conferences I should know about?",
    "How do Financial Advisors find clients?",
    "What are the top issues in the economy I should know about?",
    "How much is my company worth?",
    "Should I merge with another company?",
];

const BlogPostCards = ({ blogPosts }: { blogPosts: BlogPostEntry[] }) => (
    <div className="space-y-2">
        <span className="inline-flex items-center rounded-full bg-[#F2EAFE] px-3 py-1 text-xs font-medium text-[#3E2476]">
            From the Compass blog
        </span>
        <ul className="space-y-2">
            {blogPosts.slice(0, 5).map((bp) => {
                const mediaLabel =
                    bp.previewKind === "YOUTUBE_EMBED"
                        ? "YouTube"
                        : bp.previewKind === "VIMEO_EMBED"
                          ? "Vimeo"
                          : bp.previewKind === "VIDEO_URL" || bp.previewKind === "VIDEO_ASSET"
                            ? "Video"
                            : null;
                return (
                    <li key={bp.id}>
                        <a
                            href={`/p/${bp.slug}`}
                            className="group flex items-start gap-3 rounded-lg border border-[#D6C7F4] bg-white px-4 py-3 no-underline transition-colors hover:border-[#8B6BCB] hover:bg-[#FAF6FF]"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-[#3E2476] group-hover:text-[#1F0F4E]">
                                    {bp.title}
                                </p>
                                {bp.excerpt && (
                                    <p className="mt-0.5 line-clamp-2 text-xs text-gray-600">
                                        {bp.excerpt}
                                    </p>
                                )}
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                    {bp.authorName && <span>by {bp.authorName}</span>}
                                    {bp.publishedAt && (
                                        <span>· {formatISODateRelative(bp.publishedAt)}</span>
                                    )}
                                    {mediaLabel && (
                                        <span className="inline-flex items-center rounded-full bg-[#FFE9F0] px-2 py-0.5 text-[10px] font-medium text-[#A8336A]">
                                            {mediaLabel}
                                        </span>
                                    )}
                                    {bp.categories.slice(0, 2).map((c) => (
                                        <span
                                            key={c.slug}
                                            className="inline-flex items-center rounded-full bg-[#EEF2F7] px-2 py-0.5 text-[10px] font-medium text-[#475569]"
                                        >
                                            {c.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </a>
                    </li>
                );
            })}
        </ul>
    </div>
);

const turnVendors = (answer: TurnAnswer): Array<{ id: number; name: string }> => {
    if (answer.vendors && answer.vendors.length > 0) return answer.vendors.slice(0, 3);
    return answer.contextResults
        .filter((r) => r.entityType === "VENDOR")
        .slice(0, 3)
        .map((r) => ({ id: Number(r.id), name: r.name }))
        .filter((v) => Number.isFinite(v.id));
};

// `trigger` is still passed by Header but unused: the sheet has a single built-in trigger.
export const AskNavigator: React.FC<{ trigger?: React.ReactNode }> = () => {
    const { enqueueSnackbar } = useSnackbar();
    const utils = trpc.useUtils();
    const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);

    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [askScope, setAskScope] = useState<AskScope>("dataset");
    const [question, setQuestion] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);

    // The conversation. chatId is null until the first ask returns one.
    const [chatId, setChatId] = useState<string | null>(null);
    const [turns, setTurns] = useState<Turn[]>([]);
    const [loadingChatId, setLoadingChatId] = useState<string | null>(null);
    const turnSeq = useRef(0);
    const lastTurnRef = useRef<HTMLDivElement>(null);

    // Share mode targets one turn of the transcript.
    const [shareTurnKey, setShareTurnKey] = useState<string | null>(null);
    const [email, setEmail] = useState("");
    const [recipient, setRecipient] = useState("");
    const [emailError, setEmailError] = useState("");
    const [copiedTurnKey, setCopiedTurnKey] = useState<string | null>(null);

    // File attachment state
    const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const askNavigator = trpc.askNavigator.ask.useMutation();
    const uploadStart = trpc.userContent.upload.start.useMutation();
    const uploadComplete = trpc.userContent.upload.complete.useMutation();
    const { data: vendorData } = trpc.vendors.getVendor.useQuery(selectedVendorId ?? 0, {
        enabled: selectedVendorId !== null,
    });
    const shareAnswerMutation = trpc.askNavigator.shareAnswer.useMutation();
    const deleteChatMutation = trpc.askNavigator.deleteChat.useMutation();

    const { data: chatList } = trpc.askNavigator.listChats.useQuery(
        { limit: 10 },
        { enabled: isOpen },
    );

    const isBusy = abortController !== null;
    const shareTurn = turns.find((t) => t.key === shareTurnKey) ?? null;
    const isShareMode = shareTurn !== null && shareTurn.answer !== null;
    const lastTurn = turns.length > 0 ? turns[turns.length - 1] : null;

    // Keep the newest turn in view as it's appended and again when it resolves.
    useEffect(() => {
        if (!isOpen || isShareMode) return;
        lastTurnRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [turns.length, lastTurn?.status, isOpen, isShareMode]);

    const updateTurn = (key: string, patch: Partial<Turn>) => {
        setTurns((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));
    };

    const handleFileSelected = (file: File) => {
        if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result !== "string") return;
                setSelectedFile({ name: file.name, previewUrl: reader.result, file });
            };
            reader.readAsDataURL(file);
        } else {
            setSelectedFile({ name: file.name, previewUrl: null, file });
        }
    };

    const runAsk = async (turn: Turn, currentChatId: string | null) => {
        abortController?.abort();
        const controller = new AbortController();
        setAbortController(controller);

        try {
            let imageFileId = turn.imageFileId;
            if (!imageFileId && turn.file) {
                // PRTL-1349: a phone-sized photo is plenty for the model.
                const uploadFile = await downscaleImageForUpload(turn.file.file);
                const {
                    file: { id: fileId },
                    uploadUrl,
                } = await uploadStart.mutateAsync({
                    originalFilename: turn.file.name,
                    contentType: uploadFile.type || "application/octet-stream",
                    context: "PRIVATE_ATTACHMENT",
                });
                await fetch(uploadUrl, {
                    method: "PUT",
                    body: await uploadFile.arrayBuffer(),
                });
                await uploadComplete.mutateAsync({ fileId });
                imageFileId = fileId;
                if (!controller.signal.aborted) updateTurn(turn.key, { imageFileId });
            }

            const result = await askNavigator.mutateAsync({
                question: turn.question,
                scope: turn.scope,
                imageFileId,
                chatId: currentChatId ?? undefined,
            });
            // The turn is stored server-side even if the user stopped waiting.
            void utils.askNavigator.listChats.invalidate();
            if (controller.signal.aborted) return;
            setChatId(result.chatId);
            updateTurn(turn.key, { status: "done", answer: answerFromAsk(result), file: null });
        } catch (err: unknown) {
            if ((err instanceof Error && err.name === "AbortError") || controller.signal.aborted) {
                askNavigator.reset();
                return;
            }
            console.error("Error in Ask Navigator:", err);
            updateTurn(turn.key, { status: "error" });
        } finally {
            setAbortController((current) => (current === controller ? null : current));
        }
    };

    const handleSubmit = (text: string) => {
        if (isBusy) return;
        const normalizedQuestion = text.trim() || "I uploaded a file. Please help me with it.";
        turnSeq.current += 1;
        const turn: Turn = {
            key: `local-${turnSeq.current}`,
            question: normalizedQuestion,
            scope: askScope,
            status: "pending",
            answer: null,
            file: selectedFile,
        };
        setSelectedFile(null);
        setQuestion("");
        setTurns((prev) => [...prev, turn]);
        void runAsk(turn, chatId);
    };

    const handleRetry = (turn: Turn) => {
        if (isBusy) return;
        updateTurn(turn.key, { status: "pending" });
        void runAsk({ ...turn, status: "pending" }, chatId);
    };

    const handleStop = () => {
        abortController?.abort();
        askNavigator.reset();
        setAbortController(null);
        const pending = turns.find((t) => t.status === "pending");
        if (!pending) return;
        setTurns((prev) => prev.filter((t) => t.key !== pending.key));
        // Hand the question back so it isn't lost.
        if (!question.trim()) setQuestion(pending.question);
    };

    const handleNewChat = () => {
        abortController?.abort();
        askNavigator.reset();
        setAbortController(null);
        setChatId(null);
        setTurns([]);
        setShareTurnKey(null);
    };

    const handleOpenChat = async (id: string) => {
        if (isBusy || loadingChatId) return;
        setLoadingChatId(id);
        try {
            const chat = await utils.askNavigator.getChat.fetch({ chatId: id });
            setChatId(chat.id);
            setTurns(chat.turns.map(turnFromHistory));
            const last = chat.turns[chat.turns.length - 1];
            if (last) setAskScope(last.scope);
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Could not open that chat.", { variant: "error" });
        } finally {
            setLoadingChatId(null);
        }
    };

    const handleDeleteChat = async () => {
        if (!pendingDelete) return;
        const { id } = pendingDelete;
        try {
            await deleteChatMutation.mutateAsync({ chatId: id });
            if (chatId === id) handleNewChat();
            enqueueSnackbar("Chat deleted.", { variant: "success" });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Failed to delete chat.", { variant: "error" });
        } finally {
            setPendingDelete(null);
            void utils.askNavigator.listChats.invalidate();
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (question.trim() || selectedFile) handleSubmit(question.trim());
    };

    const handleShareByEmail = async () => {
        if (!shareTurn?.answer) return;
        const answer = shareTurn.answer;

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email.trim()) {
            setEmailError("Email is required.");
            return;
        }
        if (!emailRegex.test(email)) {
            setEmailError("Invalid email address.");
            return;
        }
        setEmailError("");

        try {
            // Prefer the TipTap doc; if a turn doesn't carry it we wrap the
            // plaintext sidecar in a single paragraph so the email still ships
            // with a body rather than failing the validator.
            const answerJsonForShare =
                answer.answerJson ??
                ({
                    type: "doc" as const,
                    content: [
                        {
                            type: "paragraph" as const,
                            content: [{ type: "text" as const, text: answer.markdownAnswer ?? "" }],
                        },
                    ],
                } satisfies TipTapDoc);
            await shareAnswerMutation.mutateAsync({
                email,
                recipientName: recipient,
                question: shareTurn.question,
                answerJson: answerJsonForShare,
                queryType: answer.queryType,
                scope: shareTurn.scope,
                contextResults: answer.contextResults,
            });
            enqueueSnackbar("Answer shared successfully!", { variant: "success" });
            setShareTurnKey(null);
            setEmail("");
            setRecipient("");
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Failed to share answer.", { variant: "error" });
        }
    };

    const handleCancelShare = () => {
        setShareTurnKey(null);
        setEmail("");
        setRecipient("");
        setEmailError("");
    };

    const handleCopy = async (turn: Turn) => {
        if (!turn.answer) return;
        // Prefer the TipTap doc so headings/bullets/tables paste as clean text
        // (no literal markdown markers); fall back to the markdown sidecar.
        const text = `Q: ${turn.question}\n\n${plainAnswer(turn.answer)}`;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedTurnKey(turn.key);
            setTimeout(
                () => setCopiedTurnKey((current) => (current === turn.key ? null : current)),
                2000,
            );
        } catch {
            enqueueSnackbar("Could not copy to clipboard", { variant: "error" });
        }
    };

    const chats = chatList?.chats ?? [];

    const suggestedQuestions = (
        <div>
            <h3 className="text-md mb-3 font-bold text-gray-700">All Suggested Questions</h3>
            <div className="grid grid-cols-2 gap-5">
                {suggestions.map((suggestion, index) => {
                    const mod = index % 6;
                    const style =
                        mod < 2
                            ? "border-[#B5D6E3] bg-[#E3F2FD] text-[#195D7A]"
                            : mod < 4
                              ? "border-[#7AD2C1] bg-[#D0F5EA] text-[#006659]"
                              : "border-[#8B6BCB] bg-[#F2EAFE] text-[#3E2476]";
                    return (
                        <button
                            key={index}
                            type="button"
                            onClick={() => handleSubmit(suggestion)}
                            disabled={isBusy}
                            className={`w-full rounded-lg border p-4 text-left text-sm transition-colors hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 ${style}`}
                        >
                            {suggestion}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const recentChats = chats.length > 0 && (
        <div>
            <h3 className="text-md mb-3 flex items-center gap-2 font-bold text-gray-700">
                <Clock className="h-4 w-4 text-gray-400" />
                Recent chats
            </h3>
            <ul className="space-y-2">
                {chats.map((chat) => (
                    <li
                        key={chat.id}
                        className="group flex items-stretch rounded-lg border border-gray-200 bg-white transition-colors hover:border-gray-300 hover:bg-gray-50"
                    >
                        <button
                            type="button"
                            onClick={() => void handleOpenChat(chat.id)}
                            disabled={isBusy || loadingChatId !== null}
                            aria-label={`Open chat: ${chat.title}`}
                            className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loadingChatId === chat.id ? (
                                <Spinner className="mt-0.5 h-4 w-4 shrink-0" />
                            ) : (
                                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 group-hover:text-gray-500" />
                            )}
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-800 group-hover:text-gray-900">
                                    {chat.title}
                                </p>
                                <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-400">
                                    <span>{formatISODateRelative(chat.lastAskedAt)}</span>
                                    <span>·</span>
                                    <span>
                                        {chat.turnCount}{" "}
                                        {chat.turnCount === 1 ? "question" : "questions"}
                                    </span>
                                </div>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setPendingDelete({ id: chat.id, title: chat.title })}
                            disabled={deleteChatMutation.isPending}
                            aria-label={`Delete chat: ${chat.title}`}
                            title="Delete chat"
                            className="shrink-0 rounded-r-lg px-3 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );

    const renderTurn = (turn: Turn, index: number) => {
        const isLast = index === turns.length - 1;
        const answer = turn.answer;
        const vendors = answer ? turnVendors(answer) : [];
        return (
            <div
                key={turn.key}
                ref={isLast ? lastTurnRef : undefined}
                className="space-y-3 scroll-mt-2"
            >
                <div className="rounded-lg border bg-blue-50 p-4">
                    <h4 className="mb-2 text-sm font-medium text-blue-900">Your question</h4>
                    <p className="text-sm whitespace-pre-wrap text-blue-800">{turn.question}</p>
                </div>

                {turn.status === "pending" && (
                    <div className="flex items-center gap-3 rounded-lg border bg-gray-50 p-4">
                        <Spinner className="h-5 w-5" />
                        <p className="text-sm text-gray-600">Thinking...</p>
                    </div>
                )}

                {turn.status === "error" && (
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                        <p className="text-sm text-red-700">
                            Something went wrong answering this question.
                        </p>
                        {isLast && (
                            <button
                                type="button"
                                onClick={() => handleRetry(turn)}
                                disabled={isBusy}
                                className="flex shrink-0 items-center gap-1 rounded-lg bg-white px-3 py-1 text-sm text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <RotateCcw className="h-3 w-3" />
                                Retry
                            </button>
                        )}
                    </div>
                )}

                {turn.status === "done" && answer && (
                    <>
                        <div className="rounded-lg border bg-gray-50 p-4">
                            <div className="mb-2 flex items-center justify-between gap-2">
                                <h4 className="text-sm font-medium text-gray-900">Answer</h4>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => void handleCopy(turn)}
                                        aria-label="Copy question and answer"
                                        className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-gray-200"
                                    >
                                        {copiedTurnKey === turn.key ? (
                                            <>
                                                <Check className="h-3 w-3 text-green-600" />
                                                <span className="text-green-600">Copied</span>
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-3 w-3" />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShareTurnKey(turn.key)}
                                        aria-label="Share this answer by email"
                                        className="flex items-center gap-1 rounded-lg bg-blue-100 px-3 py-1 text-sm text-blue-700 hover:bg-blue-200"
                                    >
                                        <Share2 className="h-3 w-3" />
                                        Share
                                    </button>
                                </div>
                            </div>
                            {answer.answerJson ? (
                                <ProseDocument doc={answer.answerJson} />
                            ) : answer.markdownAnswer ? (
                                <MarkdownContent markdown={answer.markdownAnswer} />
                            ) : (
                                <p className="text-sm text-gray-500">No answer was returned.</p>
                            )}
                        </div>

                        {answer.blogPosts && answer.blogPosts.length > 0 && (
                            <BlogPostCards blogPosts={answer.blogPosts} />
                        )}

                        {vendors.length > 0 && (
                            <div className="space-y-2">
                                <span className="inline-flex items-center rounded-full bg-[#e0f2f7] px-3 py-1 text-xs font-medium text-[#00647a]">
                                    From your vendor database
                                </span>
                                <div className="flex flex-wrap gap-2">
                                    {vendors.map((vendor) => (
                                        <button
                                            key={vendor.id}
                                            type="button"
                                            onClick={() => setSelectedVendorId(vendor.id)}
                                            className="inline-flex items-center rounded-full border border-[#0096B8] bg-white px-3 py-1.5 text-xs text-[#0096B8] shadow-xs transition-colors hover:bg-[#e0f2f7]"
                                        >
                                            {vendor.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isLast && answer.suggestedQuestions.length > 0 && (
                            <div>
                                <h3 className="mb-2 font-bold text-gray-700">
                                    Suggested Follow-up Questions
                                </h3>
                                <div className="space-y-2">
                                    {answer.suggestedQuestions.map((sq, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => handleSubmit(sq)}
                                            disabled={isBusy}
                                            className="w-full rounded-lg border border-[#B5D6E3] bg-[#E3F2FD] p-3 text-left text-sm text-[#195D7A] transition-colors hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {sq}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <div>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                    <div
                        className="flex cursor-pointer items-center gap-1.5 rounded-3xl border border-[#C9C9C9] bg-[#EEEEEE] px-2 py-1.5 text-[#5C6A6D] md:gap-2 md:px-3 lg:min-w-[268px]"
                        role="button"
                        aria-label="Ask navigator"
                    >
                        <AskNavigatorIcon className="shrink-0" />
                        <span className="hidden whitespace-nowrap md:inline">Ask navigator</span>
                    </div>
                </SheetTrigger>
                <SheetContent
                    side="right"
                    className="fixed top-0 bottom-0 h-[calc(100vh)] w-full sm:top-3 sm:bottom-3 sm:left-1/2 sm:h-[calc(100vh-1.5rem)] sm:w-[90vw] sm:max-w-176 sm:-translate-x-1/2 sm:rounded-2xl"
                >
                    <form className="flex h-full flex-col" onSubmit={handleFormSubmit}>
                        <SheetHeader className="space-y-0 gap-y-5">
                            <div className="flex items-center justify-between gap-3 pr-8">
                                <SheetTitle className="text-left align-middle font-sans text-[24px] leading-[100%] font-bold tracking-normal text-[#424242]">
                                    {isShareMode ? "Share Answer by Email" : "Ask Navigator"}
                                </SheetTitle>
                                {!isShareMode && turns.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleNewChat}
                                        className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                        <Plus className="h-4 w-4" />
                                        New chat
                                    </button>
                                )}
                            </div>
                            <hr className="mx-[-24px] my-7 border-[#c8c8c8]" />
                        </SheetHeader>

                        {!isShareMode && (
                            <div className="flex h-full min-h-0 flex-col">
                                <div className="mt-6 min-h-0 flex-1 shrink overflow-y-auto px-0">
                                    {turns.length > 0 ? (
                                        <div className="space-y-6 pb-2">
                                            {turns.map(renderTurn)}
                                        </div>
                                    ) : (
                                        <div className="space-y-6">
                                            {recentChats}
                                            {suggestedQuestions}
                                        </div>
                                    )}
                                </div>

                                {/* Composer */}
                                <div className="space-y-3 mt-4 shrink-0 border-t border-gray-200 pt-4">
                                    <TextArea
                                        id="question"
                                        aria-label={
                                            turns.length > 0
                                                ? "Ask a follow-up question"
                                                : "Ask a question"
                                        }
                                        value={question}
                                        onChange={(e) => setQuestion(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                if (question.trim() || selectedFile) {
                                                    handleSubmit(question.trim());
                                                }
                                            }
                                        }}
                                        placeholder={
                                            turns.length > 0
                                                ? "Ask a follow-up..."
                                                : "Type your question here..."
                                        }
                                        disabled={isBusy}
                                        className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-transparent focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
                                        rows={2}
                                    />

                                    {/* File preview */}
                                    {selectedFile && (
                                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
                                            {selectedFile.previewUrl ? (
                                                <img
                                                    src={selectedFile.previewUrl}
                                                    alt="Preview"
                                                    className="h-12 w-12 rounded object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-12 w-12 items-center justify-center rounded bg-gray-200">
                                                    <Paperclip className="h-5 w-5 text-gray-500" />
                                                </div>
                                            )}
                                            <span className="min-w-0 flex-1 truncate text-sm text-gray-700">
                                                {selectedFile.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedFile(null)}
                                                aria-label="Remove attached file"
                                                className="rounded-full p-1 hover:bg-gray-200"
                                            >
                                                <X className="h-4 w-4 text-gray-500" />
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isBusy}
                                            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <Paperclip className="h-4 w-4" />
                                            Attach file
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handleFileSelected(file);
                                                e.currentTarget.value = "";
                                            }}
                                            className="hidden"
                                        />
                                        <span className="text-md pr-2 pl-2 font-bold text-gray-700">
                                            Ask in
                                        </span>
                                        <ToggleGroup
                                            type="single"
                                            value={askScope}
                                            onValueChange={(value) => {
                                                if (
                                                    value === "dataset" ||
                                                    value === "internet" ||
                                                    value === "both"
                                                )
                                                    setAskScope(value);
                                            }}
                                            className="justify-start gap-2"
                                        >
                                            <ToggleGroupItem
                                                value="dataset"
                                                aria-label="Public Navigator Portal"
                                            >
                                                Public Navigator Portal
                                            </ToggleGroupItem>
                                            <ToggleGroupItem value="internet" aria-label="Internet">
                                                Internet
                                            </ToggleGroupItem>
                                            <ToggleGroupItem value="both" aria-label="Both">
                                                Both
                                            </ToggleGroupItem>
                                        </ToggleGroup>
                                    </div>

                                    <div className="flex gap-2">
                                        <Button
                                            type="submit"
                                            disabled={(!question.trim() && !selectedFile) || isBusy}
                                            variant="primary"
                                            className="flex-1"
                                        >
                                            {isBusy ? "Processing..." : "Ask Navigator"}
                                            {!isBusy && <SubmitIcon className="ml-2" />}
                                        </Button>
                                        {isBusy && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                onClick={handleStop}
                                            >
                                                Stop
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {isShareMode && shareTurn && (
                            <div className="mt-6">
                                <div className="flex-1 overflow-auto px-0">
                                    <div className="space-y-4">
                                        {/* Preview of what will be shared */}
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <p className="mb-2 text-xs font-semibold text-gray-600">
                                                Question:
                                            </p>
                                            <p className="mb-3 text-sm text-gray-800">
                                                {shareTurn.question}
                                            </p>
                                            <p className="text-xs font-semibold text-gray-600">
                                                The recipient will receive the complete answer in
                                                the email.
                                            </p>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="ask-navigator-share-email"
                                                className="mb-1 block text-sm font-medium text-gray-700"
                                            >
                                                Recipient Email *
                                            </label>
                                            <input
                                                id="ask-navigator-share-email"
                                                type="email"
                                                placeholder="Enter email address"
                                                value={email}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                    setEmailError("");
                                                }}
                                                className={`w-full rounded-lg border bg-[#eeeeee] px-3 py-2 text-sm outline-hidden ${
                                                    emailError
                                                        ? "border-red-500 focus:ring-red-500"
                                                        : "border-gray-300 focus:ring-blue-500"
                                                }`}
                                            />
                                            {emailError && (
                                                <p className="mt-1 text-sm text-red-500">
                                                    {emailError}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="ask-navigator-share-name"
                                                className="mb-1 block text-sm font-medium text-gray-700"
                                            >
                                                Recipient Name (Optional)
                                            </label>
                                            <input
                                                id="ask-navigator-share-name"
                                                type="text"
                                                placeholder="Enter recipient name"
                                                value={recipient}
                                                onChange={(e) => setRecipient(e.target.value)}
                                                className="w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm outline-hidden"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end gap-2">
                                    <Button
                                        onClick={handleCancelShare}
                                        variant="secondary"
                                        type="button"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleShareByEmail}
                                        variant="primary"
                                        type="button"
                                        disabled={shareAnswerMutation.isPending}
                                    >
                                        {shareAnswerMutation.isPending ? "Sharing..." : "Share"}
                                        {!shareAnswerMutation.isPending && (
                                            <SubmitIcon className="ml-2" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </form>
                    <DeleteDialogModal
                        open={pendingDelete !== null}
                        setOpen={(open) => !open && setPendingDelete(null)}
                        handleDelete={() => void handleDeleteChat()}
                        disabled={deleteChatMutation.isPending}
                        message={`"${pendingDelete?.title ?? ""}" and all of its questions and answers will be deleted. This cannot be undone.`}
                    />
                </SheetContent>
            </Sheet>

            {/* Vendor Details Modal */}
            {vendorData && (
                <VendorDetailedEntry
                    vendor={vendorData}
                    isOpen={selectedVendorId !== null}
                    setIsOpen={(open) => !open && setSelectedVendorId(null)}
                />
            )}
        </div>
    );
};
